import { Lightbulb, Shield } from "lucide-react";
import type { DemoPreview } from "@/src/features/demo-contracts/types";

/* ─── Derived helpers ────────────────────────────────────────────────────── */

function deriveProspectiveCustomers(preview: DemoPreview): string[] {
  const labels = new Set<string>();

  for (const painPoint of preview.painPoints) {
    for (const evidence of painPoint.evidence) {
      const author = evidence.author?.trim();
      if (!author) continue;

      if (evidence.community) {
        labels.add(`${author} in ${evidence.community}`);
        continue;
      }

      labels.add(`${author} on ${evidence.platform.toUpperCase()}`);
    }
  }

  return Array.from(labels);
}

function buildProspectiveCustomerSummary(preview: DemoPreview): Array<{
  label: string;
  count: number;
}> {
  const summary = new Map<string, number>();

  for (const painPoint of preview.painPoints) {
    for (const evidence of painPoint.evidence) {
      const key = evidence.community?.trim() || evidence.platform.toUpperCase();
      summary.set(key, (summary.get(key) ?? 0) + 1);
    }
  }

  return Array.from(summary.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((itemA, itemB) => itemB.count - itemA.count)
    .slice(0, 6);
}

export function deriveCustomerFitNarrative(
  preview: DemoPreview,
): string | null {
  const topPain = preview.painPoints[0];
  if (!topPain) return null;

  if (topPain.frequency >= 100 && topPain.severityScore >= 75) {
    return `High-frequency, high-severity pain indicates a large audience actively seeking a solution — strong ICP match.`;
  }

  if (topPain.severityScore >= 80) {
    return `Critical-severity pain signals an acute problem that drives urgency — buyers are motivated to act quickly.`;
  }

  if (topPain.frequency >= 50) {
    return `Frequent mentions suggest a well-known friction point — buyers recognize the problem and are aware solutions exist.`;
  }

  return null;
}

export function deriveIdeaCustomerAlignment(
  preview: DemoPreview,
): Array<{ ideaTitle: string; alignmentNote: string }> {
  if (!preview.generatedIdeas || preview.generatedIdeas.length === 0) {
    return [];
  }

  return preview.generatedIdeas.slice(0, 3).map((idea) => {
    const ideaText = [
      idea.problemItSolves ?? "",
      idea.solutionApproach ?? "",
      idea.description ?? "",
    ]
      .join(" ")
      .toLowerCase();

    const matchedPain = preview.painPoints.find((pp) => {
      const painKeywords = pp.title.toLowerCase().split(" ");
      return painKeywords.some(
        (word) => word.length > 4 && ideaText.includes(word),
      );
    });

    if (matchedPain) {
      return {
        ideaTitle: idea.title,
        alignmentNote: `Targets users facing: ${matchedPain.title} (${matchedPain.frequency} mentions)`,
      };
    }

    return {
      ideaTitle: idea.title,
      alignmentNote: `Targets users with general ${preview.nicheSlug.replace(/-/g, " ")} friction`,
    };
  });
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function CustomerFitNarrative({ preview }: { preview: DemoPreview }) {
  const narrative = deriveCustomerFitNarrative(preview);
  if (!narrative) return null;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
        <Shield className="size-3" aria-hidden="true" />
        Audience fit signal
      </p>
      <p className="mt-1 font-mono text-[10px] leading-[1.6] text-primary-foreground/60">
        {narrative}
      </p>
    </div>
  );
}

function IdeaAlignmentNote({ preview }: { preview: DemoPreview }) {
  const alignments = deriveIdeaCustomerAlignment(preview);
  if (alignments.length === 0) return null;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
        <Lightbulb className="size-3" aria-hidden="true" />
        Idea-to-audience fit
      </p>
      <div className="mt-1.5 space-y-1">
        {alignments.map(({ ideaTitle, alignmentNote }) => (
          <p
            key={ideaTitle}
            className="font-mono text-[10px] leading-[1.6] text-primary-foreground/58"
          >
            <span className="text-primary-foreground/60">{ideaTitle}</span>:{" "}
            {alignmentNote}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ─── Main section ───────────────────────────────────────────────────────── */

export function CustomersSection({ preview }: { preview: DemoPreview }) {
  const prospectiveCustomerSummary = buildProspectiveCustomerSummary(preview);
  const prospectiveCustomers = deriveProspectiveCustomers(preview);

  return (
    <section className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:space-y-4">
      {/* Header */}
      <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
        Who seems to feel this pain
      </p>
      <p className="font-mono text-[12px] leading-[1.7] text-primary-foreground/70 sm:text-[13px]">
        Use this section to understand which audience pockets appear most often
        in the evidence and whether they look urgent enough to target.
      </p>

      {/* Summary grid */}
      {prospectiveCustomerSummary.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {prospectiveCustomerSummary.map((item) => (
            <article
              key={item.label}
              className="rounded-lg border border-white/10 bg-black/20 p-3"
            >
              <p className="font-mono text-[12px] text-primary-foreground/68 sm:text-[13px]">
                Evidence cluster: {item.label}
              </p>
              <p className="mt-1 font-mono text-[11px] text-primary-foreground/52">
                {item.count} citation{item.count > 1 ? "s" : ""}
              </p>
            </article>
          ))}
        </div>
      ) : (
        <p className="font-mono text-[11px] text-primary-foreground/45">
          This preview does not include enough author or community detail to
          profile the audience yet.
        </p>
      )}

      {/* Example users */}
      {prospectiveCustomers.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/30">
            Example people behind the quotes
          </p>
          <ul className="mt-1.5 space-y-1.5">
            {prospectiveCustomers.slice(0, 4).map((customer) => (
              <li
                key={customer}
                className="font-mono text-[12px] leading-[1.8] text-primary-foreground/68 sm:text-[13px]"
              >
                {customer}
              </li>
            ))}
          </ul>
        </div>
      )}

      <CustomerFitNarrative preview={preview} />
      <IdeaAlignmentNote preview={preview} />

      {/* ICP profiles */}
      {preview.idealCustomers && preview.idealCustomers.length > 0 ? (
        <section className="space-y-2 rounded-lg border border-white/10 bg-black/20 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
            Draft customer profiles
          </p>
          <div className="space-y-2">
            {preview.idealCustomers.map((customer) => (
              <details
                key={customer.id}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5"
              >
                <summary className="cursor-pointer list-none font-mono text-[12px] text-primary-foreground/72 sm:text-[13px]">
                  {customer.segment} — urgency {customer.urgency}
                </summary>
                <div className="mt-2 space-y-1.5 font-mono text-[12px] leading-[1.8] text-primary-foreground/64 sm:text-[13px]">
                  <p>Role: {customer.role ?? "N/A"}</p>
                  <p>Industry: {customer.industry ?? "N/A"}</p>
                  <p>Pain points: {customer.pains.join("; ")}</p>
                  <p>
                    Frustrators: {customer.frustrators?.join("; ") ?? "N/A"}
                  </p>
                  <p>Goals: {customer.goals?.join("; ") ?? "N/A"}</p>
                  <p>Budget: {customer.budget ?? "N/A"}</p>
                  <p>Timeline: {customer.timeline ?? "N/A"}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      ) : (
        <section className="rounded-lg border border-white/10 bg-black/15 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
            Draft customer profiles
          </p>
          <p className="mt-1 font-mono text-[11px] text-primary-foreground/48">
            Live analysis adds fuller customer profiles once the report has
            enough grounded evidence.
          </p>
        </section>
      )}
    </section>
  );
}
